import { TFunction } from "i18next";
import { Weekdays } from "../../../../Domain/Types/Booking/Weekdays.type";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { Status } from "../../../../Domain/Types/Status";
import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../../../../../features/Booking-Form/typings/booking.types";
import { IPlaceInventory } from "../../../../Domain/Types/FloorPlan/PlaceInventory.type";
import { IZoneInventory } from "../../../../Domain/Types/FloorPlan/ZoneInventory.type";
import { VehicleInventory } from "../../../../../FleetManagement/types/VehicleInventory.types";

/**
 * Location id from place/zone/vehicle inventory only (no booking-form `selectedLocation`).
 * Use when booking Redux state is not tied to the UI (e.g. schedule calendar dialog with minimal `inputs`).
 * Falls back through floorInventory when zone/place omit nested location (common on schedule API).
 */
export function resolveLocationInventoryIdFromInventory(
  inv: IPlaceInventory | IZoneInventory | VehicleInventory | undefined
): number | undefined {
  if (!inv) {
    return undefined;
  }
  const placeId = (inv as IPlaceInventory).locationInventoryId;
  if (placeId != null && placeId > 0) {
    return placeId;
  }
  const nestedId = inv.locationInventory?.id;
  if (nestedId != null && nestedId > 0) {
    return nestedId;
  }
  const floorInv = (inv as IPlaceInventory | IZoneInventory).floorInventory;
  const floorLocId = floorInv?.locationInventoryId;
  if (floorLocId != null && floorLocId > 0) {
    return floorLocId;
  }
  const floorNestedId = floorInv?.locationInventory?.id;
  if (floorNestedId != null && floorNestedId > 0) {
    return floorNestedId;
  }
  return undefined;
}

/**
 * Location id for service-admin checks from a schedule row: prefers top-level `locationInventoryId`
 * when the API provides it, then {@link resolveLocationInventoryIdFromInventory} on `bookingInventory`.
 */
export function resolveLocationInventoryIdFromSchedule(
  schedule: Pick<BookingScheduleInterface, "bookingInventory" | "locationInventoryId">
): number | undefined {
  const fromSchedule = schedule.locationInventoryId;
  if (fromSchedule != null && fromSchedule > 0) {
    return fromSchedule;
  }
  return resolveLocationInventoryIdFromInventory(schedule.bookingInventory);
}

/**
 * Location id for service-admin checks: `selectedLocation` from booking inputs first, then selected place/zone inventory.
 */
export function resolveLocationInventoryIdForServiceAdmin(
  bookingInputs: BookingInputs,
  selectedEntry: { inventory?: IPlaceInventory | IZoneInventory } | undefined
): number | undefined {
  const fromSelectedLocation = bookingInputs.selectedLocation;
  if (fromSelectedLocation != null && fromSelectedLocation > 0) {
    return fromSelectedLocation;
  }
  return resolveLocationInventoryIdFromInventory(selectedEntry?.inventory);
}

export function hasSelectedWeekdays(weekdays: Weekdays): boolean {
  return Object.values(weekdays).some(Boolean);
}

// returns undefined if all weekdays are falsy
export function checkWeekdays(weekdays: Weekdays): Weekdays | undefined {
  return hasSelectedWeekdays(weekdays) ? weekdays : undefined;
}

/**
 * @description previously, used to check if workplace selectable for user
 * const uid = usersBookedFor.length > 0 ? usersBookedFor[userIndex]?.userId : "";
 * const checkIfWpSelectable = checkIfWorkplaceSelectableForUser(userId,uid,statusInformationData).length;
 */
export function generateBtnText(
  selectableByVariant: boolean,
  bookingInputs: BookingInputs,
  approvalRequired: boolean,
  userIndex: number,
  userId: string,
  statusInformationData: Status | undefined,
  t: TFunction
) {
  const { usersBookedFor, mode: bookingMode } = bookingInputs;

  if (bookingMode === BookingType.TEAM || bookingMode === BookingType.VISITOR) {
    const userSelectionName =
      usersBookedFor.length > 0 && usersBookedFor[userIndex].firstName
        ? usersBookedFor[userIndex].firstName
        : "Colleague";

    if (!selectableByVariant)
      return t("You can't select this Place for {{name}}", { name: userSelectionName });
    if (approvalRequired) return t("Request place for {{name}}", { name: userSelectionName });

    return t("Select for {{name}}", { name: userSelectionName });
  } else if (!selectableByVariant) {
    /**
     * @deprecated used to display excluded days but deprecated for now
     * if (checkIfWpSelectable > 0) return `${checkIfWpSelectable} ${t("day(s) excluded")}`; else
     */
    return t(
      `You can't select this ${bookingMode === BookingType.CONFERENCEZONE ? "Room" : "Place"}`
    );
  } else if (approvalRequired) {
    const entryItem = bookingMode === BookingType.CONFERENCEZONE ? "room" : "place";
    return t(`Request this ${entryItem}`);
  } else if (bookingMode === BookingType.CONFERENCEZONE) {
    return t("Select this Room");
  }
  return t("Select this place");
}

export const checkIfWorkplaceSelectableForUser = (
  bookingUser: string,
  userSelectingFor: string,
  status: Status | undefined
): string[] => {
  if (status) {
    const occupiedStates = status.status
      .filter(
        state =>
          state.occupiedStatus.length !== 0 &&
          !state.occupiedStatus.some(
            o => o.userInfo.userId === userSelectingFor || o.userInfo.userId === bookingUser
          )
      )
      .map(state => state.bookingDay);

    const permittedStates = status.status
      .filter(
        state =>
          state.permittedStatus?.length &&
          !state.permittedStatus?.some(
            p => p.userInfo.userId === userSelectingFor || p.userInfo.userId === bookingUser
          )
      )
      .map(state => state.bookingDay);

    const restrictedStates = status.status
      .filter(
        state =>
          state.restrictedStatus?.length !== 0 &&
          !state?.restrictedStatus?.some(
            r => r.userInfo.userId === userSelectingFor || r.userInfo.userId === bookingUser
          )
      )
      .map(state => state.bookingDay);

    const disabledStates = status.status
      .filter(day => day.disabledStatus?.some(state => state.startTime))
      .map(state => state.bookingDay);

    const notBookableDates: string[] = [
      ...occupiedStates,
      ...permittedStates,
      ...restrictedStates,
      ...disabledStates
    ];

    return notBookableDates;
  } else return [];
};

export const occupiedFound = (occupiedlength: number, t: TFunction) => {
  if (occupiedlength > 1) {
    return t("MultiBookingConflictsFound", { amount: occupiedlength });
  }
  return t("SingleBookingConflictFound", { amount: occupiedlength });
};
